import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import OrgOnboardingModal from "@/components/onboarding/OrgOnboardingModal";
import OrgOnboardingBanner from "@/components/onboarding/OrgOnboardingBanner";
import { useOrgOnboardingStatus } from "@/hooks/useOrgOnboardingStatus";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { announceOrgWizardOpen, onOrgSetupRelaunch } from "@/lib/orgSetupRelaunch";

// The org-side nudge banner lives on the admin feed. The admin home is now the
// profile editor (/admin/profile), but the banner deliberately does not follow
// it there: the editor renders its own completeness UI via useOrgProfileStrength,
// and a second nudge on the same screen would double up.
const BANNER_ROUTES = ["/admin/feed"];

// Auth utility routes where the session is authenticated but the user is
// mid-flow (e.g. setting a new password). The wizard must not pop over these.
const SUPPRESS_AUTO_OPEN = ["/reset-password", "/auth/callback", "/auth/social/callback"];

// Org-side mirror of AgentOnboardingGate — single app-level mount for the org
// onboarding wizard. Gated by useOrgOnboardingStatus, which now reads profile
// strength rather than a completion flag; it is inert for agents, signed-out
// users, and admins acting in agent mode.
const OrgOnboardingGate = () => {
  const { settled, showBanner, allowAutoOpen, org, refetchOrg } = useOrgOnboardingStatus();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  // Auto-open at most once per session — after a dismissal the banner takes over
  // the nudging. Seeded from sessionStorage so a reload within the same tab
  // does not re-open. The seven-day snooze in orgOnboardingDecision is the
  // cross-session guard; this is the within-session one.
  const [autoOpened, setAutoOpened] = useState(
    () => sessionStorage.getItem("compaire_org_onboarding_autoopened") === "1"
  );

  useEffect(() => {
    const isPreview = new URLSearchParams(location.search).get("preview") === "true";
    if (
      allowAutoOpen &&
      !autoOpened &&
      !SUPPRESS_AUTO_OPEN.includes(location.pathname) &&
      !isPreview
    ) {
      setIsOpen(true);
      setAutoOpened(true);
      sessionStorage.setItem("compaire_org_onboarding_autoopened", "1");
    }
  }, [allowAutoOpen, autoOpened, location.pathname, location.search]);

  // Deliberate relaunch from an admin surface (the completeness card's "Resume
  // setup"). Ignores the snooze and the once-per-session guard — the admin asked.
  useEffect(() => onOrgSetupRelaunch(() => setIsOpen(true)), []);

  // Tell surfaces that write the same org row (the profile editor mounted
  // underneath) when the wizard takes and releases it. The modal renders only
  // when `org` is present, so an open with no org is not announced.
  useEffect(() => {
    announceOrgWizardOpen(isOpen && !!org);
  }, [isOpen, org]);

  return (
    <>
      <OrgOnboardingModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onComplete={refetchOrg}
        org={org}
        refetchOrg={refetchOrg}
        ink={FEATURE_FLAGS.ORG_WORKSPACE_V2}
      />
      {settled && showBanner && !isOpen && BANNER_ROUTES.includes(location.pathname) && (
        <OrgOnboardingBanner onStart={() => setIsOpen(true)} />
      )}
    </>
  );
};

export default OrgOnboardingGate;
